import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, KeyRound, ExternalLink, FlaskConical, Trash2, Plus, Link2, Globe, FileText, FileType2, Loader2 } from "lucide-react";

interface RefSource { id: string; url: string; label: string | null; created_at: string; }
interface RefFile { id: string; kind: "pdf" | "txt"; label: string | null; char_count: number; created_at: string; }

const AdminAISettings = () => {
  const [configured, setConfigured] = useState<boolean>(false);
  const [maskedKey, setMaskedKey] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sources, setSources] = useState<RefSource[]>([]);
  const [files, setFiles] = useState<RefFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [addingSrc, setAddingSrc] = useState(false);

  const loadKeyStatus = async () => {
    const { data } = await (supabase as any).rpc("get_admin_setting_status", { _key: "openai_api_key" });
    const status = (data ?? {}) as { configured?: boolean; masked?: string | null };
    setConfigured(!!status.configured);
    setMaskedKey(status.masked ?? "");
    setLoaded(true);
  };

  useEffect(() => {
    loadKeyStatus();
    loadSources();
    loadFiles();
  }, []);

  const loadSources = async () => {
    const { data } = await (supabase as any)
      .from("admin_reference_sources")
      .select("id,url,label,created_at")
      .order("created_at", { ascending: false });
    setSources((data as RefSource[]) || []);
  };

  const loadFiles = async () => {
    const { data } = await (supabase as any)
      .from("admin_reference_files")
      .select("id,kind,label,char_count,created_at")
      .order("created_at", { ascending: false });
    setFiles((data as RefFile[]) || []);
  };

  const uploadFile = async (file: File, kind: "pdf" | "txt") => {
    if (file.size > 10 * 1024 * 1024) { toast.error("Fichier > 10 Mo"); return; }
    setUploading(true);
    try {
      const buf = await file.arrayBuffer();
      let bin = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const base64 = btoa(bin);
      const { data, error } = await supabase.functions.invoke("extract-reference-file", {
        body: { kind, label: file.name, filename: file.name, base64 },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`${kind.toUpperCase()} importé (${(data as any)?.file?.char_count ?? 0} caractères extraits)`);
      loadFiles();
    } catch (e: any) {
      toast.error(e?.message || "Échec de l'import");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (id: string) => {
    const { error } = await (supabase as any).from("admin_reference_files").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setFiles((s) => s.filter((x) => x.id !== id));
  };

  const addSource = async () => {
    const url = newUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error("URL invalide (doit commencer par http:// ou https://)");
      return;
    }
    setAddingSrc(true);
    const { error } = await (supabase as any)
      .from("admin_reference_sources")
      .insert({ url, label: newLabel.trim() || null });
    setAddingSrc(false);
    if (error) return toast.error(error.message);
    setNewUrl("");
    setNewLabel("");
    toast.success("Source ajoutée");
    loadSources();
  };

  const removeSource = async (id: string) => {
    const { error } = await (supabase as any).from("admin_reference_sources").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setSources((s) => s.filter((x) => x.id !== id));
  };

  const save = async () => {
    const v = value.trim();
    if (!v) {
      toast.error("Veuillez saisir une clé valide.");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("save-openai-key", {
      body: { action: "save", key: v },
    });
    setSaving(false);
    if (error) return toast.error(error.message);
    if ((data as any)?.error) return toast.error((data as any).error);
    setValue("");
    setConfigured(true);
    setMaskedKey(((data as any)?.masked as string) || "");
    toast.success("Clé OpenAI enregistrée. Le navigateur n'en garde aucune copie.");
  };

  const remove = async () => {
    if (!confirm("Supprimer la clé OpenAI ? Les générations IA repasseront sur le crédit Lovable.")) return;
    const { data, error } = await supabase.functions.invoke("save-openai-key", {
      body: { action: "delete" },
    });
    if (error) return toast.error(error.message);
    if ((data as any)?.error) return toast.error((data as any).error);
    setConfigured(false);
    setMaskedKey("");
    setValue("");
    toast.success("Clé supprimée.");
  };

  const testKey = async () => {
    const v = value.trim();
    if (!v) {
      toast.error("Saisissez d'abord une clé.");
      return;
    }
    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("test-openai-key", {
        body: { key: v },
      });
      if (error) {
        toast.error(`Échec : ${error.message}`);
        return;
      }
      const r = data as { valid?: boolean; error?: string; status?: number };
      if (r?.valid) toast.success("Clé OpenAI valide ✓");
      else toast.error(`Échec${r?.status ? ` (${r.status})` : ""} : ${(r?.error || "clé invalide").slice(0, 200)}`);
    } catch (e) {
      toast.error("Erreur réseau : " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card/50 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-accent" />
              IA & Intégrations
            </h3>
            <p className="font-body text-sm text-muted-foreground mt-1">
              Configurez votre clé OpenAI (gpt-4o-mini) et la base de sites de référence que l'IA
              analysera pour enrichir chaque article du Guide.
            </p>
          </div>
          {loaded && (
            <Badge variant={configured ? "default" : "secondary"} className="shrink-0">
              {configured ? "Clé configurée ✓" : "Crédit Lovable"}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-accent" /> Clé API OpenAI
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="gemini-key">Clé API</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="gemini-key"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={configured ? (maskedKey || "sk-•••••••••••") : "sk-..."}
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
              />
            </div>
            {configured && (
              <p className="text-xs text-emerald-500 mt-1 font-mono">
                Clé actuelle : {maskedKey || "configurée ✓"} — jamais réaffichée en clair.
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Obtenez votre clé sur{" "}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline inline-flex items-center gap-1"
              >
                OpenAI Platform <ExternalLink className="w-3 h-3" />
              </a>
              . Modèle utilisé : <code className="text-foreground">gpt-4o-mini</code>.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
            <Button onClick={testKey} disabled={testing} variant="outline">
              <FlaskConical className="w-4 h-4 mr-2" /> {testing ? "Test en cours…" : "Tester la clé"}
            </Button>
            {configured && (
              <Button onClick={remove} variant="ghost" className="text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Globe className="w-4 h-4 text-accent" /> Sources et Inspiration Web
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Ajoutez des URLs de sites de référence (blogs concurrents, Wikipédia, offices de tourisme…).
            Lors de la génération d'un article, l'IA analysera ceux dont l'URL ou le contenu correspond
            au mot-clé pour s'en inspirer (sans plagiat).
          </p>
          <div className="space-y-2">
            <Label htmlFor="src-url">URL du site</Label>
            <Input
              id="src-url"
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://exemple.com/article-de-reference"
              className="font-mono text-xs"
            />
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Étiquette (optionnel) — ex: Wikipedia Essaouira"
            />
            <Button onClick={addSource} disabled={addingSrc} size="sm" className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-1" /> Ajouter un lien
            </Button>
            <div className="flex gap-2 pt-1">
              <label className="inline-flex">
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, "pdf"); e.target.value = ""; }}
                />
                <Button asChild size="sm" variant="outline" disabled={uploading}>
                  <span className="cursor-pointer">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileType2 className="w-4 h-4 mr-1" />}
                    + PDF
                  </span>
                </Button>
              </label>
              <label className="inline-flex">
                <input
                  type="file"
                  accept="text/plain,.txt"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, "txt"); e.target.value = ""; }}
                />
                <Button asChild size="sm" variant="outline" disabled={uploading}>
                  <span className="cursor-pointer">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                    + TXT
                  </span>
                </Button>
              </label>
            </div>
          </div>

          <div className="space-y-1 max-h-[260px] overflow-y-auto pr-1 border-t border-border pt-3">
            {sources.length === 0 && files.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucune source enregistrée.</p>
            ) : (<>
            {files.map((f) => (
              <div key={f.id} className="flex items-center gap-2 text-xs bg-muted/40 rounded px-2 py-1.5">
                {f.kind === "pdf" ? <FileType2 className="w-3 h-3 text-accent shrink-0" /> : <FileText className="w-3 h-3 text-accent shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">{f.label || `Fichier ${f.kind.toUpperCase()}`}</div>
                  <div className="text-muted-foreground">{f.kind.toUpperCase()} · {f.char_count.toLocaleString()} caractères</div>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive shrink-0" onClick={() => removeFile(f.id)} aria-label="Supprimer">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
            {sources.map((s) => (
              <div key={s.id} className="flex items-center gap-2 text-xs bg-muted/40 rounded px-2 py-1.5">
                <Link2 className="w-3 h-3 text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  {s.label && <div className="font-medium text-foreground truncate">{s.label}</div>}
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent truncate block">
                    {s.url}
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive hover:text-destructive shrink-0"
                  onClick={() => removeSource(s.id)}
                  aria-label="Supprimer"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
            </>)}
          </div>
        </CardContent>
      </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-base">Comment ça marche</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>Modèle utilisé :</strong> <code className="text-foreground">gpt-4o-mini</code> (rapide et économique, facturation OpenAI).
          </p>
          <p>
            <strong>Fonctions concernées :</strong> génération d'articles Guide, régénération
            par IA (slug verrouillé), fixes SEO automatiques, recherche de mots-clés.
          </p>
          <p>
            <strong>Sans clé :</strong> le site continue d'utiliser le crédit Lovable AI s'il est disponible.
          </p>
          <p>
            <strong>Génération d'images :</strong> reste sur Lovable AI / Pexels (OpenAI gpt-4o-mini est utilisé uniquement pour le texte).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAISettings;